import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import {
	AutotaskConnectionListItemModel,
	AutotaskConnectionSaveModel,
	create,
	get,
	update
} from "client/Api/AutotaskConfiguration/autotaskConfiguration";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	Dialog,
	Form,
	FormItem,
	FormattedText,
	SecretValueInput,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";

export type AutotaskConnectionDialogProps = {
	connectionId?: number;
	onAfterSave(item: AutotaskConnectionListItemModel): void;
	onClose(): void;
};

export const AutotaskConnectionDialog = (props: AutotaskConnectionDialogProps): ReactElement => {
	const {
		connectionId,
		onAfterSave,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [model, setModel] = useState<AutotaskConnectionSaveModel>(initialModel);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<AutotaskConnectionSaveModel> = useFormHandler<AutotaskConnectionSaveModel>(
		model,
		setModel,
		validationConfiguration
	);

	const stableOnClose: typeof onClose = useStableImperativeCallback(onClose);

	useEffect(
		(): EffectWithDestructorResult => {
			if (connectionId === undefined) {
				return undefined;
			}

			const loadConnection = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const {
						name,
						username
					} = await get(
						connectionId,
						signal
					);

					const newModel: AutotaskConnectionSaveModel = {
						name,
						username,
						password: {
							containsNewValue: false,
							value: null
						}
					};

					setModel(newModel);

					setIsBusy(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			};

			const cancellation = new AbortController();
			loadConnection(cancellation.signal);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			connectionId,
			stableOnClose
		]
	);

	const isNewConnection: boolean = connectionId === undefined;

	let titleKey: LocalizationKey;
	let saveKey: LocalizationKey;
	if (isNewConnection) {
		titleKey = "_NewNameConnection";
		saveKey = "_Add";
	} else {
		titleKey = "_EditNameConnection";
		saveKey = "_Save";
	}

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			let id: number | undefined = connectionId;
			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				if (id === undefined) {
					id = (await create(model, globalCancellation))
						.id;
				} else {
					await update(
						id,
						model,
						globalCancellation
					);
				}

				onClose();
				onAfterSave({
					id,
					name: model.name,
					serverUrl: model.username
				});
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_ChangesNotSaved"));
				}
				setIsBusy(false);
			}
		},
		[
			alertManager,
			connectionId,
			formHandler.validation,
			globalCancellation,
			isBusy,
			model,
			onAfterSave,
			onClose
		]
	);

	return (
		<Dialog hasBusyIndicator={isBusy} trackingId="autotask-connection-dialog">
			<Dialog.Header
				title={
					<FormattedText
						text={localize(titleKey)}
						thingsToReplace={[
							{ what: "{{Name}}", filler: localize("_Autotask")}
						]}
					/>
				}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_Name")} isRequired>
						<TextInput {...formHandler.for("name").attachToTextInput()} />
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_Username")} isRequired>
						<TextInput {...formHandler.for("username").attachToTextInput()} />
						<ValidationError {...formHandler.for("username").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_Password")} isRequired>
						<SecretValueInput {...formHandler.for("password").attachToSecretValueInput()}
							containsInitialValue={!isNewConnection}
						/>
						<ValidationError {...formHandler.for("password").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize(saveKey)}
						onClick={submitHandler}
						trackingId="save"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
AutotaskConnectionDialog.displayAutotaskConnectionDialog = "AutotaskConnectionDialog";

const validationConfiguration: IValidationConfiguration<AutotaskConnectionSaveModel> =
	createValidationConfiguration<AutotaskConnectionSaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forScalar("username")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forObject("password")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

const initialModel: AutotaskConnectionSaveModel = {
	name: "",
	username: "",
	password: {
		containsNewValue: true,
		value: ""
	}
};